import React from 'react';
import { Auction } from '../../types/auction.types';
import { SaleSelection } from '../../hooks/useSaleSelection';
import { deriveSaleState, SALE_STATE_LABEL, normalizeStatus } from '../../utils/saleState';

/**
 * WHICH SALE AM I LOOKING AT — said out loud, on every operator page.
 *
 * 🔴 This is the actual fix, not the resolver.
 *
 * The Shipping Station shipped against a hardcoded June auction id from June
 * until August. The default being wrong is survivable — you notice, you switch.
 * What let it rot for two months is that the page RENDERED THE SALE'S OWN TITLE
 * from the doc it had been told to load, so it looked entirely correct. There
 * was nothing on screen that could be wrong-looking.
 *
 * So this component states the sale unconditionally, and when the sale on screen
 * is not the one the resolver would pick, it says that too — permanently, in
 * amber, with one click to move. Not a toast, not a first-render-only notice:
 * something that is still there on the fortieth box of the afternoon.
 *
 * A wrong default you can see is a five-second fix. A wrong default you cannot
 * see is two months.
 */

const STATE_STYLE: Record<string, string> = {
  live: 'bg-green-100 text-green-800',
  closing: 'bg-amber-100 text-amber-800',
  ended: 'bg-gray-100 text-gray-700',
  confirmed: 'bg-blue-100 text-blue-800',
  scheduled: 'bg-purple-100 text-purple-800',
  draft: 'bg-gray-100 text-gray-500',
};

const StateBadge: React.FC<{ auction: Auction }> = ({ auction }) => {
  // Drafts and 'scheduled' are stored truths; live/closing/ended are derived from
  // lot timing. deriveSaleState already owns that distinction — don't re-derive it.
  const state = deriveSaleState(auction);
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${STATE_STYLE[state] || 'bg-gray-100 text-gray-700'}`}>
      {SALE_STATE_LABEL[state] || normalizeStatus(auction.status)}
    </span>
  );
};

export interface SaleContextBannerProps {
  selection: SaleSelection;
  /** Shown above the selector, e.g. "Shipping". Optional. */
  label?: string;
  /**
   * Override what choosing a sale does. Needed by route-driven pages: on
   * /auctions/:auctionId/invoices the route id outranks a pin, so the selection
   * has to be made by NAVIGATING, not by writing a pin the route would beat.
   * Applies to both the dropdown and the Switch button.
   */
  onSelect?: (id: string) => void;
  className?: string;
}

const SaleContextBanner: React.FC<SaleContextBannerProps> = ({ selection, label, onSelect, className = '' }) => {
  const { auctions, auction, saleId, resolvedId, isPinned, reason, loading, error, setSale, resetToResolved } = selection;
  const choose = (id: string) => (onSelect ? onSelect(id) : setSale(id));
  const goToResolved = () => (onSelect && resolvedId ? onSelect(resolvedId) : resetToResolved());

  const resolvedAuction = auctions.find((a) => a.id === resolvedId) || null;

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow p-4 text-sm text-gray-500 ${className}`}>
        Loading sales…
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-red-50 border border-red-200 rounded-lg p-4 ${className}`}>
        <p className="text-sm font-semibold text-red-800">Could not load the sale list.</p>
        <p className="text-sm text-red-700 mt-1">{error}</p>
        <p className="text-xs text-red-600 mt-2">
          Nothing below is scoped to a sale until this resolves — reload before acting on it.
        </p>
      </div>
    );
  }

  if (!auction) {
    return (
      <div className={`bg-amber-50 border border-amber-200 rounded-lg p-4 ${className}`}>
        <p className="text-sm font-semibold text-amber-900">No sale selected.</p>
        <p className="text-sm text-amber-800 mt-1">
          {auctions.length
            ? 'No sale qualifies as current — every sale is a draft, has not started, or has no end time on record. Pick one explicitly.'
            : 'There are no sales in this project.'}
        </p>
        {auctions.length > 0 && (
          <select
            value=""
            onChange={(e) => e.target.value && choose(e.target.value)}
            className="input py-2 mt-3 max-w-md"
            aria-label="Select a sale"
          >
            <option value="">Choose a sale…</option>
            {auctions.map((a) => (
              <option key={a.id} value={a.id}>{a.title || a.id}</option>
            ))}
          </select>
        )}
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow overflow-hidden ${className}`}>
      <div className="p-4 flex flex-wrap items-center gap-x-4 gap-y-3">
        <div className="min-w-0">
          {label && <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">{label}</p>}
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-bold truncate" title={auction.title}>{auction.title || auction.id}</h2>
            <StateBadge auction={auction} />
          </div>
          <p className="text-xs font-mono text-gray-400 mt-0.5">{saleId}</p>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <label htmlFor="sale-context-select" className="text-sm text-gray-600">Sale</label>
          <select
            id="sale-context-select"
            value={saleId}
            onChange={(e) => choose(e.target.value)}
            className="input py-2 max-w-xs"
          >
            {auctions.map((a) => (
              <option key={a.id} value={a.id}>{a.title || a.id}</option>
            ))}
          </select>
        </div>
      </div>

      {/*
        The whole point. Persistent — it does not dismiss, because the condition
        it reports does not go away on acknowledgement, only on being fixed.
      */}
      {isPinned && resolvedAuction && (
        <div className="bg-amber-50 border-t border-amber-200 px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="text-amber-900 text-sm">
            You are on <strong>{auction.title || auction.id}</strong> — the current sale is{' '}
            <strong>{resolvedAuction.title || resolvedAuction.id}</strong>.
          </span>
          <button
            type="button"
            onClick={goToResolved}
            className="text-sm font-semibold px-3 py-1 rounded bg-amber-600 text-white hover:bg-amber-700"
          >
            Switch to {resolvedAuction.title || resolvedAuction.id}
          </button>
          {reason && (
            <span className="text-xs text-amber-700 basis-full" title={reason}>
              {reason}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default SaleContextBanner;
